import type { Policy, PolicyLevel } from '../types';
import { buildStablePolicyId } from './policyId';

const API_BASE = (process.env.REACT_APP_API_URL ?? '').trim().replace(/\/$/, '');
const USE_REMOTE_API = Boolean(API_BASE);
const MOCK_POLICIES_JSON = `${process.env.PUBLIC_URL ?? ''}/data/policies.json`;

let mockCache: Policy[] | null = null;

async function fetchJson(url: string) {
  const res = await fetch(url, { cache: 'no-cache' });
  if (!res.ok) throw new Error(`请求失败 ${res.status}: ${url}`);
  return res.json();
}

function toTrimString(raw: unknown): string {
  if (raw == null) return '';
  return String(raw).trim();
}

function toStringArray(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((item) => toTrimString(item)).filter(Boolean);
  }
  const value = toTrimString(raw);
  if (!value) return [];
  return value
    .split(/[,，;；]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizeLevel(raw: unknown): PolicyLevel {
  const value = toTrimString(raw).toLowerCase();
  if (value === 'national' || value === '国家级') return 'national';
  if (value === 'park' || value === '园区' || value === '园区级') return 'park';
  if (value === 'city' || value === '地级市' || value === '市级') return 'city';
  return 'provincial';
}

function normalizePolicy(item: Record<string, unknown>): Policy {
  const level = normalizeLevel(item.level ?? item['政策层级'] ?? item['政策等级']);
  const province = toTrimString(item.province ?? item['省份']);
  const city = toTrimString(item.city ?? item['市'] ?? item['市区名称']);
  const parkName = toTrimString(item.parkName ?? item['园区名称']);
  const resolvedParkName = level === 'park' ? parkName : '';
  return {
    id: buildStablePolicyId(level, province, city, resolvedParkName),
    title: toTrimString(item.title ?? item['政策标题']),
    level,
    policyTier: toTrimString(item.policyTier ?? item['policyTier'] ?? item['政策等级']),
    province,
    city,
    ...(resolvedParkName ? { parkName: resolvedParkName } : {}),
    issuingOffice: toTrimString(item.issuingOffice ?? item['发文部门']),
    issuedAt: toTrimString(item.issuedAt ?? item['发文日期'] ?? item['发布时间']),
    targetScopes: toStringArray(item.targetScopes ?? item['适用对象']),
    industryTags: toStringArray(item.industryTags ?? item['产业标签']),
    supportTypes: toStringArray(item.supportTypes ?? item['支持方向']),
    summary: toTrimString(item.summary ?? item['核心条款摘要']),
    coreGoal: toTrimString(item.coreGoal ?? item['核心目标']),
    supportPolicy: toTrimString(item.supportPolicy ?? item['支持政策']),
    applicationSchedule: toTrimString(item.applicationSchedule ?? item['申报安排']),
    relatedFiles: toStringArray(item.relatedFiles ?? item['相关文件']),
    keywords: toStringArray(item.keywords ?? item['关键词']),
  };
}

async function loadMockFromPublicJson(): Promise<Policy[]> {
  if (mockCache) return mockCache;
  const json = await fetchJson(MOCK_POLICIES_JSON);
  if (!Array.isArray(json)) throw new Error('政策数据格式异常');
  mockCache = json.map((item) => normalizePolicy(item));
  return mockCache;
}

async function fetchAllPoliciesRemote(): Promise<Policy[]> {
  const res = await fetch(`${API_BASE}/policies`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`获取政策列表失败 ${res.status}`);
  const data = await res.json();
  const items = Array.isArray(data) ? data : data.items;
  if (!Array.isArray(items)) throw new Error('政策接口响应格式异常');
  return items.map((item: Record<string, unknown>) => normalizePolicy(item));
}

export async function fetchAllPolicies(): Promise<Policy[]> {
  if (USE_REMOTE_API) return fetchAllPoliciesRemote();
  return loadMockFromPublicJson();
}

export function clearPoliciesMockCache(): void {
  mockCache = null;
}
